<template>
    <div class="flex flex-col gap-6">
        <table-toolbar
            :show-title="true"
            title="Contacts"
            :show-count="true"
            :current-count="filteredContacts.length"
            :total-count="contactData.length"
            :searchable="true"
            :search-query="searchQuery"
            @search-change="searchQuery = $event"
            :show-view-toggle="true"
            :view-mode="layout"
            @view-mode-change="layout = $event"
            :show-refresh="false"
            :per-page="12"
            :show-per-page-selector="false"
        >
            <template #action-buttons>
                <themed-button 
                    v-if="canCreateContact"
                    @click="showNewContact"
                    variant="default"
                >
                    <theme-icon icon="add-icon" type="icons" classes="flex" />
                    <span>New Contact</span>
                </themed-button>
            </template>
        </table-toolbar>

        <!-- Grid View -->
        <themed-grid
            v-if="layout === 'grid'"
            :data="filteredContacts"
            :fields="[]"
            :columns="4"
            :per-page="12"
            empty-message="No contacts found"
            empty-icon="user"
            :clickable="false"
            custom-class="contacts-grid"
        >
            <template #default="{ item: contact }">
                <!-- Badge -->
                <div 
                    v-if="contact.type" 
                    class="contacts-tab__badge text-xs"
                    :class="getBadgeClass(contact.type)"
                >
                    {{ contact.type }}
                </div>

                <!-- Header with Avatar and Name -->
                <div class="contacts-tab__card-header">
                    <div class="contacts-tab__avatar text-md">
                        <img 
                            v-if="contact.avatar" 
                            :src="contact.avatar.view_url" 
                            :alt="contact.name"
                            class="contacts-tab__avatar-img"
                        />
                        <span v-else>{{ getInitials(contact.name) }}</span>
                    </div>
                    <div class="contacts-tab__info">
                        <h4 class="contacts-tab__name text-base">{{ contact.name }}</h4>
                        <p v-if="contact.title" class="contacts-tab__title text-sm">{{ contact.title }}</p>
                    </div>
                </div>

                <!-- Company -->
                <div v-if="contact.company_name" class="contacts-tab__company">
                    <theme-icon icon="building" type="icons" classes="contacts-tab__company-icon" />
                    <p class="contacts-tab__company-name text-sm">{{ contact.company_name }}</p>
                </div>

                <!-- Contact Details -->
                <div class="contacts-tab__details">
                    <div v-if="contact.email" class="contacts-tab__detail-item">
                        <theme-icon icon="mail" type="icons" classes="contacts-tab__detail-icon" />
                        <a :href="'mailto:' + contact.email" class="contacts-tab__detail-link text-sm">
                            {{ contact.email }}
                        </a>
                    </div>
                    <div v-if="contact.cell" class="contacts-tab__detail-item">
                        <theme-icon icon="phone" type="icons" classes="contacts-tab__detail-icon" />
                        <span class="contacts-tab__detail-text text-sm">{{ contact.cell }}</span>
                    </div>
                    <div v-else-if="contact.phone" class="contacts-tab__detail-item">
                        <theme-icon icon="phone" type="icons" classes="contacts-tab__detail-icon" />
                        <span class="contacts-tab__detail-text text-sm">
                            {{ contact.phone }}<span v-if="contact.extension"> x{{ contact.extension }}</span>
                        </span>
                    </div>
                </div>

                <!-- Actions -->
                <div class="contacts-tab__actions">
                    <themed-button 
                        v-if="canEditContact"
                        @click="editContact(contact)" 
                        variant="outline"
                        size="sm"
                        class="contacts-tab__action-button"
                    >
                        <theme-icon icon="edit-icon" type="icons" classes="w-4 h-4" />
                        <span>Edit</span>
                    </themed-button>
                    <themed-button 
                        v-if="canDeleteContact"
                        @click="confirmDeleteContact(contact)" 
                        variant="outline"
                        size="sm"
                        class="contacts-tab__action-button contacts-tab__action-button--delete"
                    >
                        <theme-icon icon="trash-icon" type="icons" classes="w-4 h-4" />
                    </themed-button>
                </div>
            </template>
        </themed-grid>

        <!-- Table View -->
        <themed-table
            v-else
            :columns="columns"
            :data="filteredContacts"
            :loading="loading"
            :pagination="false"
            :searchable="false"
            :sortable="true"
            :show-row-details="true"
            row-details-title="Contact Details"
            @action="handleAction"
        >
            <template #column-contact="{ row }">
                <div class="flex items-center gap-3">
                    <div class="flex-shrink-0 w-10 h-10 rounded-full overflow-hidden flex items-center justify-center bg-muted">
                        <img 
                            v-if="row.avatar" 
                            :src="row.avatar.view_url" 
                            :alt="row.name"
                            class="w-full h-full object-cover"
                        />
                        <theme-icon 
                            v-else 
                            icon="user" 
                            type="icons" 
                            classes="w-6 h-6 text-muted-foreground"
                        />
                    </div>
                    <div class="flex flex-col gap-1 min-w-0">
                        <div class="font-semibold whitespace-nowrap overflow-hidden text-ellipsis">{{ row.name }}</div>
                        <div v-if="row.type" class="text-sm text-muted-foreground">{{ row.type }}</div>
                    </div>
                </div>
            </template>
        </themed-table>

        <!-- Contact Modal (handles both create and edit) -->
        <contact-modal
            :mode="selectedContact ? 'edit' : 'create'"
            :contact="selectedContact"
            :attach-to="attachTo"
            :attach-to-id="attachToId"
            @contact-created="handleContactSaved"
            @contact-updated="handleContactSaved"
        />
    </div>
</template>

<script>
import { mapState } from 'vuex';
import TableToolbar from '../table/TableToolbar.vue';
import ThemedTable from '../ThemedTable.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemedGrid from '../ThemedGrid.vue';
import ThemeIcon from '../ThemeIcon.vue';
import ContactModal from './ContactModal.vue';

export default {
    name: 'ContactsV1',
    
    components: {
        TableToolbar,
        ThemedTable,
        ThemedButton,
        ThemedGrid,
        ThemeIcon,
        ContactModal
    },

    props: {
        contactData: {
            type: Array,
            default: () => []
        },
        attachTo: {
            type: String,
            required: true
        },
        attachToId: {
            type: Number,
            required: true
        }
    },

    data() {
        return {
            loading: false,
            selectedContact: null,
            layout: 'grid',
            searchQuery: '',
            columns: [
                {
                    field: 'contact',
                    label: 'CONTACT',
                    sortable: true,
                    sortField: 'name'
                    // Using slot: complex HTML (avatar + name + type badge)
                },
                {
                    field: 'title',
                    label: 'TITLE',
                    sortable: true,
                    formatter: (value) => value || '-'
                },
                {
                    field: 'company_name',
                    label: 'COMPANY',
                    sortable: true,
                    formatter: (value) => value || '-'
                },
                {
                    field: 'email',
                    label: 'EMAIL',
                    sortable: true,
                    formatter: (value) => {
                        return value 
                            ? `<a href="mailto:${value}" class="font-medium text-primary no-underline hover:underline">${value}</a>`
                            : '-';
                    }
                },
                {
                    field: 'phone',
                    label: 'PHONE',
                    sortable: false,
                    align:'right',
                    formatter: (value) => value || '-'
                },
                {
                    field: 'extension',
                    label: 'EXT.',
                    align:'right',
                    sortable: false,
                    formatter: (value) => value || '-'
                },
                {
                    field: 'cell',
                    label: 'CELL',
                    align:'right',
                    sortable: false,
                    formatter: (value) => value || '-'
                },
                {
                    field: 'comments',
                    label: 'COMMENTS',
                    sortable: false,
                    showInTable: false,
                    formatter: (value) => value || '-'
                },
                {
                    field: 'actions',
                    label: '',
                    type: 'actions',
                    sortable: false,
                    showInTable: true,
                    buttons: [
                        {
                            name: 'edit',
                            label: 'Edit',
                            icon: 'edit',
                            variant: 'ghost',
                            size: 'sm',
                            iconOnly: true,
                            visible: () => this.canEditContact
                        },
                        {
                            name: 'delete',
                            label: 'Delete',
                            icon: 'trash',
                            variant: 'ghost',
                            size: 'sm',
                            iconOnly: true,
                            visible: () => this.canDeleteContact
                        }
                    ]
                }
            ]
        };
    },

    computed: {
        ...mapState(['contacts']),

        filteredContacts() {
            // Use Vuex state as the source of truth, not the prop
            let contacts = this.contacts.filtered || [];
            
            if (this.searchQuery) {
                const query = this.searchQuery.toLowerCase();
                contacts = contacts.filter(contact => {
                    return (
                        contact.name?.toLowerCase().includes(query) ||
                        contact.email?.toLowerCase().includes(query) ||
                        contact.phone?.toLowerCase().includes(query) ||
                        contact.cell?.toLowerCase().includes(query) ||
                        contact.title?.toLowerCase().includes(query) ||
                        contact.type?.toLowerCase().includes(query) ||
                        contact.company_name?.toLowerCase().includes(query)
                    );
                });
            }
            
            return contacts;
        },

        canCreateContact() {
            const route = window.location.pathname;
            if (route.includes('/sites/')) {
                return this.$gate.can('create', 'site');
            } else if (route.includes('/projects/')) {
                return this.$gate.can('create', 'project');
            }
            return false;
        },

        canEditContact() {
            const route = window.location.pathname;
            if (route.includes('/sites/')) {
                return this.$gate.can('edit', 'site');
            } else if (route.includes('/projects/')) {
                return this.$gate.can('edit', 'project');
            }
            return false;
        },

        canDeleteContact() {
            const route = window.location.pathname;
            if (route.includes('/sites/')) {
                return this.$gate.can('delete', 'site');
            } else if (route.includes('/projects/')) {
                return this.$gate.can('delete', 'project');
            }
            return false;
        }
    },

    mounted() {
        // Initialize Vuex state with prop data (create copies, not references)
        this.contacts.all = [...(this.contactData || [])];
        this.contacts.filtered = [...(this.contactData || [])];
    },

    methods: {
        getInitials(name) {
            if (!name) return '?';
            const parts = name.split(' ');
            if (parts.length >= 2) {
                return (parts[0][0] + parts[1][0]).toUpperCase();
            }
            return name.substring(0, 2).toUpperCase();
        },

        getBadgeClass(type) {
            if (!type) return 'contacts-tab__badge--default';
            
            const typeLower = type.toLowerCase();
            if (typeLower.includes('primary')) {
                return 'contacts-tab__badge--primary';
            } else if (typeLower.includes('agent')) {
                return 'contacts-tab__badge--agent';
            } else if (typeLower.includes('maintenance')) {
                return 'contacts-tab__badge--maintenance';
            }
            return 'contacts-tab__badge--default';
        },

        handleAction({ action, row }) {
            switch (action) {
                case 'edit':
                    this.editContact(row);
                    break;
                case 'delete':
                    this.confirmDeleteContact(row);
                    break;
            }
        },

        showNewContact() {
            this.selectedContact = null;
            this.$modal.show('contact-new');
        },

        editContact(contact) {
            this.selectedContact = contact;
            this.contacts.selectedContact = contact;
            this.$modal.show('contact-edit');
        },

        confirmDeleteContact(contact) {
            this.$swal({
                title: 'Delete this contact?',
                text: "ALL data will be lost",
                type: 'warning',
                buttonsStyling: false,
                confirmButtonText: 'Yes - Delete it',
                confirmButtonClass: 'btn btn-red',
                showCancelButton: true,
                cancelButtonClass: 'btn btn-secondary mr-4',
                reverseButtons: true
            }).then((result) => {
                if (result.value) {
                    this.deleteContact(contact);
                }
            });
        },

        deleteContact(contact) {
            this.$store.dispatch('deleteContact', {
                attachedTo: this.attachTo,
                attachedToId: this.attachToId,
                contactId: contact.id
            }).then(() => {
                this.$notify({
                    type: 'success',
                    title: 'Success',
                    text: 'Contact Deleted'
                });
            }).catch(error => {
                console.error(error);
                this.$notify({
                    type: 'error',
                    title: 'Error',
                    text: error.response?.data?.message || 'Failed to delete contact'
                });
            });
        },

        handleContactSaved(contact) {
            // Check if contact exists (update) or is new (create)
            const indexAll = this.contacts.all.findIndex(c => c.id === contact.id);
            const indexFiltered = this.contacts.filtered.findIndex(c => c.id === contact.id);
            
            if (indexAll !== -1) {
                // Update existing contact
                this.$set(this.contacts.all, indexAll, contact);
                if (indexFiltered !== -1) {
                    this.$set(this.contacts.filtered, indexFiltered, contact);
                }
                this.$emit('contact-updated', contact);
            } else {
                // Add new contact
                this.contacts.all.push(contact);
                this.contacts.filtered.push(contact);
                this.$emit('contact-created', contact);
            }
        }
    }
};
</script>
